import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
} from "@mui/material";
import {
  UploadCloud,
  Folder,
  FileText,
  ChevronRight,
  ChevronDown,
  Trash2,
  Search,
  Database,
  X,
  Edit2,
  ArrowLeft,
  Settings,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";
import { sampleData } from "../OnBoardingProduct/dummyData";
import { Toaster } from "react-hot-toast";
import ColumnSelector from "../DataProfiling/ColumnSelector";
import { dummyColumnSelectionData } from "../DataProfiling/dummyProfilingData";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Skeleton from "@mui/material/Skeleton";
import ColumnSelection from "./ColumnSelection";
import ConfigPopup from "./ConfigPopup";
import heartLoading from "../../../assests/heartLoading.gif";
import preloader from "../../../assests/preloader1.gif";
import CircularProgress from "@mui/material/CircularProgress";

const getIcon = (item, isOpen) => {
  if (item.children) {
    return isOpen ? (
      <ChevronDown className="h-5 " />
    ) : (
      <ChevronRight className="h-5 " />
    );
  }
  return <div />;
};

const filterTree = (items, searchTerm) => {
  if (!searchTerm) return items;
  const lowerSearch = searchTerm.toLowerCase();
  return items
    .map((item) => {
      if (item.children) {
        const filteredChildren = filterTree(item.children, searchTerm);
        if (
          filteredChildren.length > 0 ||
          item.name.toLowerCase().includes(lowerSearch)
        ) {
          return { ...item, children: filteredChildren };
        }
        return null;
      } else {
        if (item.name.toLowerCase().includes(lowerSearch)) {
          return item;
        }
        return null;
      }
    })
    .filter(Boolean);
};

// Helper to build full path recursively
const buildFullPath = (item, parentPath = "") => {
  const current = item.name || "";
  if (parentPath) {
    return `${parentPath}/${current}`;
  }
  return current;
};

// Helper to get all expanded folder paths for search
function getExpandedPaths(items, searchTerm, parentPath = "") {
  let expanded = new Set();
  const lowerSearch = searchTerm.toLowerCase();
  for (const item of items) {
    const fullPath =
      item.full_path || (parentPath ? `${parentPath}/${item.name}` : item.name);
    if (item.children) {
      const childExpanded = getExpandedPaths(
        item.children,
        searchTerm,
        fullPath
      );
      if (
        childExpanded.size > 0 ||
        item.name.toLowerCase().includes(lowerSearch)
      ) {
        expanded.add(fullPath);
        for (const p of childExpanded) expanded.add(p);
      }
    } else if (item.name.toLowerCase().includes(lowerSearch)) {
      if (parentPath) expanded.add(parentPath);
    }
  }
  return expanded;
}

const TreeItem = ({
  item,
  source,
  onSelect,
  selectedItems,
  level = 0,
  searchTerm,
  parentPath = "",
  expandedPaths = new Set(),
}) => {
  const fullPath = item.full_path || buildFullPath(item, parentPath);
  const shouldBeOpen = expandedPaths.has(fullPath);
  // Local state only for non-search mode
  const [manualOpen, setManualOpen] = React.useState(false);
  const isSearching = !!searchTerm;
  const isOpen = isSearching ? shouldBeOpen : manualOpen;

  const isSelected = selectedItems.some(
    (selected) => {
      // Source must match first
      if (selected.source !== source) return false;
      
      // PRIORITY 1: Exact name match (most reliable for user experience)
      if (item.name && selected.name && item.name === selected.name) {
        return true;
      }
      
      // Normalize paths for comparison - handle various separators and formats
      const normalizePath = (path) => {
        if (!path) return '';
        // Convert all common separators to / and lowercase for universal comparison
        return path.replace(/\./g, '/').replace(/\\/g, '/').toLowerCase().trim();
      };
      
      // Get all possible path variations
      const itemPaths = [
        item.full_path,
        fullPath,
        item.path
      ].filter(Boolean);
      
      const selectedPaths = [
        selected.path,
        selected.full_path
      ].filter(Boolean);
      
      // PRIORITY 2: Try exact path matches
      for (const itemPath of itemPaths) {
        for (const selectedPath of selectedPaths) {
          if (itemPath === selectedPath) {
            return true;
          }
        }
      }
      
      // PRIORITY 3: Try normalized path matches (handle different separators and case)
      const normalizedItemPaths = itemPaths.map(normalizePath);
      const normalizedSelectedPaths = selectedPaths.map(normalizePath);
      
      for (const normalizedItemPath of normalizedItemPaths) {
        for (const normalizedSelectedPath of normalizedSelectedPaths) {
          if (normalizedItemPath && normalizedSelectedPath && 
              normalizedItemPath === normalizedSelectedPath) {
            return true;
          }
        }
      }
      
      // PRIORITY 4: Check if selected path ends with item name or path
      // This handles cases where saved path includes parent hierarchy
      const itemName = item.name ? item.name.toLowerCase() : '';
      for (const selectedPath of selectedPaths) {
        const normalizedSelected = normalizePath(selectedPath);
        
        // Check if path ends with item name
        if (itemName && normalizedSelected.endsWith('/' + itemName)) {
          return true;
        }
        
        // Check if path ends with any item path
        for (const itemPath of itemPaths) {
          const normalizedItem = normalizePath(itemPath);
          if (normalizedSelected && normalizedItem && 
              normalizedSelected.endsWith(normalizedItem)) {
            return true;
          }
        }
      }
      
      return false;
    }
  );

  const handleSelect = (e) => {
    e.stopPropagation();
    // For ADLS: allow folders and files. For UC: only files/tables
    if (source === "adls" || !item.children || item.type === "table") {
      onSelect(
        { path: fullPath, name: item.name, source, type: item.type || 'folder' },
        !isSelected
      );
    }
  };

  const toggleOpen = () => {
    if (item.children) {
      if (!isSearching) {
        setManualOpen((prev) => !prev);
      }
      // If searching, do nothing (controlled by expandedPaths)
    }
  };

  if (
    searchTerm &&
    !item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (!item.children || item.children.length === 0)
  ) {
    return null;
  }
  return (
    <div>
      <div
        className={`flex items-center p-1.5 rounded-md cursor-pointer hover:bg-gray-100`}
        style={{ paddingLeft: `${level * 20 + 8}px` }}
        onClick={toggleOpen}
      >
        {getIcon(item, isOpen)}
        {(source === "adls" || !item.children || item.type === "table") && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleSelect}
            onClick={(e) => e.stopPropagation()}
            className="mx-2 h-4  rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
        )}
        {item.type === "table" || (!item.children && item.full_path) ? (
          <FileText className="h-5 mr-2 text-green-500" />
        ) : source === "UC" && item.children ? (
          <Database className="h-5  mr-2 text-blue-500" />
        ) : (
          <Folder className="h-5  mr-2 text-yellow-500" />
        )}
        <span className="truncate" title={item.name}>
          {item.name}
        </span>
      </div>
      {isOpen && item.children && (
        <div>
          {item.children.map((child, index) => (
            <TreeItem
              key={index}
              item={child}
              source={source}
              onSelect={onSelect}
              selectedItems={selectedItems}
              level={level + 1}
              searchTerm={searchTerm}
              parentPath={fullPath}
              expandedPaths={expandedPaths}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const TABS = [
  { key: "adls", label: "ADLS" },
  { key: "uc", label: "Unity Catalog" },
  { key: "manual", label: "Manual Upload" },
];

const AssetSelectionModal = ({
  id,
  selectedSources = [],
  nextStep,
  previousStep,
  region,
  country,
  columnScreen,
}) => {
  // console.log("selectedSources prop received:", selectedSources);

  // Only show tabs for selected sources, default to first selected
  const availableTabs = TABS.filter((tab) => selectedSources.includes(tab.key));
  console.log("available tabs", availableTabs);
  console.log("TABS array:", TABS);
  console.log("selectedSources array:", selectedSources);
  const [activeTab, setActiveTab] = useState(availableTabs[0]?.key || "adls");

  const [data, setData] = useState({ ADLS: [], UC: [] });
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const [editingNames, setEditingNames] = useState({});
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configuringFileId, setConfiguringFileId] = useState(null);
  const [isFile, setIsFile] = useState(false);
  const [configuringFileName, setConfiguringFileName] = useState(null);
  const [configuringFileSource, setConfiguringFileSource] = useState(null);
  const [fileConfigurations, setFileConfigurations] = useState({});
  const [isEditingExistingConfig, setIsEditingExistingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({
    assetName: "",
    assetDescription: "",
    dataAssetOwner: "",
    dataframeName: "",
    region: "",
    market: "",
    technicalDataSteward: "",
    completeness: "",
    accuracy: "",
    consistency: "",
    uniqueness: "",
    validity: "",
    sampleSize: 10,
    tags: [],
    // New fields for Asset Ingestion Schedule Information
    frequency: "",
    startDate: "",
    endDate: "",
    time: "",
    daysOfWeek: [],
    dayOfMonth: "",
    selectedPeriods: [],
  });
  const [formErrors, setFormErrors] = useState({});
  const [columnModalOpen, setColumnModalOpen] = useState(
    columnScreen ? true : false
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [onboardindDetails, setOnboardingDetails] = useState({});
  const [regionsCountriesData, setRegionsCountriesData] = useState([]);
  const [columnSubmitting, setColumnSubmitting] = useState(false);
  const [columnSearch, setColumnSearch] = useState(() => {
    const initial = {};
    dummyColumnSelectionData.forEach((file) => {
      initial[file.name] = "";
    });
    return initial;
  });

  useEffect(() => {
    const fetchEditData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/edit-form/${id}`
        );
        if (response.status === 200 && response.data) {
          const data = response.data;
          // Map API response to formData, skipping 'N/A' values
          // setApiAssetSources(data?.assetSource)
          setOnboardingDetails(data);
          // setCountry(data?.marketCountry)
          // setRegion(data?.marketRegion)
        }
      } catch (error) {
        toast.error("Failed to fetch product for editing");
      } finally {
        setLoading(false);
      }
    };
    fetchEditData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const adlsData = useMemo(() => data.ADLS || [], [data.ADLS]);
  const ucData = useMemo(() => data.UC || [], [data.UC]);
  const [columnsList, setColumnList] = useState([]);

  const expandedPaths = useMemo(() => {
    if (!searchTerm) return new Set();
    const currentData =
      activeTab === "adls" ? adlsData : activeTab === "uc" ? ucData : [];
    return getExpandedPaths(currentData, searchTerm);
  }, [searchTerm, adlsData, ucData, activeTab]);

  console.log("data in details", onboardindDetails);

  const [assetNameCheckMessage, setAssetNameCheckMessage] = useState("");
  const [assetNameIsUnique, setAssetNameIsUnique] = useState(undefined);
  const [disableAllFields, setDisableAllFields] = useState(false);
  const [allFieldsValid, setAllFieldsValid] = useState(true);
  const [formTouched, setFormTouched] = useState(false);
  const [ErrorMessage, SetErrorMessage] = useState({});
  console.log("error message", ErrorMessage);
  let debounceTimeout = null;

  useEffect(() => {
    let isMounted = true;
    const fetchAllData = async () => {
      setLoading(true);
      try {
        // Fetch browsing data, user selection, and regions/countries in parallel
        const [browsingRes, selectionRes, regionsCountriesRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/assets/list/${id}`),
          axios.get(
            `${process.env.REACT_APP_API_URL}/get-data-asset-details/${id}`
          ),
          axios.get(`${process.env.REACT_APP_API_URL}/regions-countries-list/`),
        ]);

        // Handle browsing data
        let processedData = { ADLS: [], UC: [] };
        SetErrorMessage(browsingRes.data?.message || {});
        if (browsingRes.data) {
          if (browsingRes.data.adls || browsingRes.data.ADLS) {
            processedData.ADLS =
              browsingRes.data.adls || browsingRes.data.ADLS || [];
          }
          if (browsingRes.data.uc || browsingRes.data.UC) {
            processedData.UC = browsingRes.data.uc || browsingRes.data.UC || [];
          }
          if (Array.isArray(browsingRes.data)) {
            processedData.ADLS = browsingRes.data;
          }
        }
        if (isMounted) setData(processedData);

        // Handle regions and countries data
        if (regionsCountriesRes.data && isMounted) {
          setRegionsCountriesData(regionsCountriesRes.data);
        }

        // Handle user selection data
        if (selectionRes.data) {
          const { selectedFolders = [], uploadedFiles = [] } = selectionRes.data;
          if (isMounted) {
            // Normalize saved items to ensure they have the correct structure
            const normalizedSelectedFolders = selectedFolders.map(item => ({
              ...item,
              path: item.path || item.full_path || item.name, // Ensure path exists
              source: item.source || 'adls', // Ensure source exists  
              type: item.type || 'table', // Ensure type exists
              full_path: item.full_path || item.path // Ensure full_path exists
            }));
            
            setSelectedFolders(normalizedSelectedFolders);
            setUploadedFiles(uploadedFiles);
            // Pre-populate fileConfigurations
            const newConfigs = {};
            normalizedSelectedFolders.forEach((f) => {
              if (f.configuration) newConfigs[f.path] = f.configuration;
            });
            uploadedFiles.forEach((f) => {
              if (f.configuration) newConfigs[f.id || f.name] = f.configuration;
            });
            setFileConfigurations(newConfigs);
          }
        } else {
          if (isMounted) {
            setSelectedFolders([]);
            setUploadedFiles([]);
            setFileConfigurations({});
          }
        }
      } catch (error) {
        toast.error("Failed to fetch asset or selection data.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchAllData();
    return () => {
      isMounted = false;
    };
  }, [id]);

  // When selectedSources changes, update activeTab to first available
  useEffect(() => {
    if (availableTabs.length > 0) {
      setActiveTab(availableTabs[0].key);
    }
  }, [selectedSources]);

  const processFiles = (files) => {
    const newFiles = Array.from(files).map((file) => ({
      file,
      id: `${file.name}-${file.lastModified}-${Math.random()}`,
      // defaultName: file.name,
      name: file.name,
      // isEditing: false,
      size: file.size,
      lastModified: file.lastModified,
      type: file.type,
    }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const csvFiles = Array.from(e.dataTransfer.files).filter((file) =>
        file.name.endsWith(".csv")
      );
      if (csvFiles.length > 0) {
        processFiles(csvFiles);
      } else {
        toast.error("Only CSV files are accepted.");
      }
      e.dataTransfer.clearData();
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
  };

  const handleItemSelect = (item, isSelected) => {
    setSelectedFolders((prev) => {
      if (isSelected) {
        return [
          ...prev,
          {
            ...item,
            // defaultName: item.name,
            // assetName: item.name,
            // isEditing: false,
          },
        ];
      }
      
      // When unchecking, use robust matching to find and remove the item
      return prev.filter((selected) => {
        // Same source must match
        if (selected.source !== item.source) return true; // Keep items from different sources
        
        // Priority 1: Name match - if names match and source matches, remove it
        if (selected.name && item.name && selected.name === item.name) {
          return false; // Remove this item
        }
        
        // Priority 2: Exact path match
        if (selected.path === item.path || selected.full_path === item.path || 
            selected.path === item.full_path) {
          return false; // Remove this item
        }
        
        // Priority 3: Normalized path match
        const normalizePath = (path) => {
          if (!path) return '';
          return path.replace(/\./g, '/').replace(/\\/g, '/').toLowerCase().trim();
        };
        
        const normalizedSelectedPath = normalizePath(selected.path);
        const normalizedItemPath = normalizePath(item.path);
        const normalizedItemFullPath = normalizePath(item.full_path);
        
        if ((normalizedSelectedPath && normalizedItemPath && normalizedSelectedPath === normalizedItemPath) ||
            (normalizedSelectedPath && normalizedItemFullPath && normalizedSelectedPath === normalizedItemFullPath)) {
          return false; // Remove this item
        }
        
        return true; // Keep this item
      });
    });
  };

  // Fixed version of the functions - ADLS includes folders, UC only files/tables
  const getAllSelectableItems = (items, source, parentPath = "") => {
    let selectable = [];
    const traverse = (item, currentParentPath = "") => {
      // Build the full path the same way TreeItem does
      const fullPath = item.full_path || buildFullPath(item, currentParentPath);
      
      // For ADLS: include all items. For UC: only files/tables
      if (source === "adls" || !item.children || item.type === "table") {
        selectable.push({
          path: fullPath,
          name: item.name,
          source,
          type: item.type || 'folder',
          full_path: fullPath
        });
      }
      if (item.children) {
        item.children.forEach(child => traverse(child, fullPath));
      }
    };
    items?.forEach(item => traverse(item, parentPath));
    return selectable;
  };

  const handleSelectAll = () => {
    const currentData =
      activeTab === "adls" ? adlsData : activeTab === "uc" ? ucData : [];
    const allItems = getAllSelectableItems(currentData, activeTab);
    const newSelected = allItems.filter(
      (item) => !selectedFolders.some((sel) => sel.path === item.path)
    );
    setSelectedFolders((prev) => [...prev, ...newSelected]);
  };

  const handleClear = () => {
    const source = activeTab;
    setSelectedFolders((prev) => prev.filter((item) => item.source !== source));
  };

  const handleRemoveFolder = (path) => {
    setSelectedFolders((prev) => prev.filter((folder) => folder.path !== path));
  };

  const handleRemoveFile = (id) => {
    setUploadedFiles((prev) => prev.filter((fileObj) => fileObj.id !== id));
  };

  const handleSave = async () => {
    
    // Validation check
    const allItemsHaveNames =
      selectedFolders.every((folder) => folder.assetName?.trim()) &&
      uploadedFiles.every((fileObj) => fileObj.assetName?.trim());
    // if (!allItemsHaveNames) {
    //   toast.error("All items must have names before saving");
    //   return;
    // }
    const formData = new FormData();
    uploadedFiles.forEach((fileObj) => {
      formData.append("files", fileObj.file);
    });
    formData.append("product_id", id);
    const payload = {
      selectedFolders: selectedFolders.map((folder) => ({
        ...folder,
        path: folder.path || folder.full_path || folder.name,
        configuration: {
          assetName:
            fileConfigurations[folder.path]?.assetName || folder.assetName,
          assetDescription:
            fileConfigurations[folder.path]?.assetDescription ||
            folder.assetDescription,
          dataAssetOwner:
            fileConfigurations[folder.path]?.dataAssetOwner ||
            folder.dataAssetOwner,
          dataframeName:
            fileConfigurations[folder.path]?.dataframeName ||
            folder.dataframeName,
          region: fileConfigurations[folder.path]?.region || folder.region,
          market: fileConfigurations[folder.path]?.market || folder.market,
          technicalDataSteward:
            fileConfigurations[folder.path]?.technicalDataSteward ||
            folder.technicalDataSteward,
          completeness:
            fileConfigurations[folder.path]?.completeness ||
            folder.completeness,
          uniqueness:
            fileConfigurations[folder.path]?.uniqueness ||
            folder.uniqueness,
          validity:
            fileConfigurations[folder.path]?.validity ||
            folder.validity,
          accuracy:
            fileConfigurations[folder.path]?.accuracy || folder.accuracy,
          consistency:
            fileConfigurations[folder.path]?.consistency || folder.consistency,
          sampleSize:
            fileConfigurations[folder.path]?.sampleSize ||
            folder.sampleSize ||
            10,
          tags: fileConfigurations[folder.path]?.tags || folder.tags || [],
          // Asset Ingestion Schedule Information
          frequency:
            fileConfigurations[folder.path]?.frequency ||
            folder.frequency,
          startDate:
            fileConfigurations[folder.path]?.startDate ||
            folder.startDate,
          endDate:
            fileConfigurations[folder.path]?.endDate ||
            folder.endDate,
          time:
            fileConfigurations[folder.path]?.time ||
            folder.time || "",
          daysOfWeek:
            fileConfigurations[folder.path]?.daysOfWeek ||
            folder.daysOfWeek || [],
          dayOfMonth:
            fileConfigurations[folder.path]?.dayOfMonth ||
            folder.dayOfMonth || "",
          selectedPeriods:
            fileConfigurations[folder.path]?.selectedPeriods ||
            folder.selectedPeriods || [],
        },
      })),
      uploadedFiles: uploadedFiles.map((file) => ({
        ...file,
        configuration: {
          assetName: fileConfigurations[file.id]?.assetName || file.assetName,
          assetDescription:
            fileConfigurations[file.id]?.assetDescription ||
            file.assetDescription,
          dataAssetOwner:
            fileConfigurations[file.id]?.dataAssetOwner || file.dataAssetOwner,
          dataframeName:
            fileConfigurations[file.id]?.dataframeName || file.dataframeName,
          region: fileConfigurations[file.id]?.region || file.region,
          market: fileConfigurations[file.id]?.market || file.market,
          technicalDataSteward:
            fileConfigurations[file.id]?.technicalDataSteward ||
            file.technicalDataSteward,
          completeness:
            fileConfigurations[file.id]?.completeness || file.completeness,
          uniqueness:
            fileConfigurations[file.id]?.uniqueness || file.uniqueness,
          validity:
            fileConfigurations[file.id]?.validity || file.validity,
          accuracy: fileConfigurations[file.id]?.accuracy || file.accuracy,
          consistency:
            fileConfigurations[file.id]?.consistency || file.consistency,
          sampleSize:
            fileConfigurations[file.id]?.sampleSize || file.sampleSize || 10,
          tags: fileConfigurations[file.id]?.tags || file.tags || [],
          // Asset Ingestion Schedule Information
          frequency:
            fileConfigurations[file.id]?.frequency ||
            file.frequency,
          startDate:
            fileConfigurations[file.id]?.startDate ||
            file.startDate,
          endDate:
            fileConfigurations[file.id]?.endDate ||
            file.endDate,
          time:
            fileConfigurations[file.id]?.time ||
            file.time || "",
          daysOfWeek:
            fileConfigurations[file.id]?.daysOfWeek ||
            file.daysOfWeek || [],
          dayOfMonth:
            fileConfigurations[file.id]?.dayOfMonth ||
            file.dayOfMonth || "",
          selectedPeriods:
            fileConfigurations[file.id]?.selectedPeriods ||
            file.selectedPeriods || [],
        },
      })),
      timestamp: new Date().toISOString(),
      totalItems: selectedFolders.length + uploadedFiles.length,
      product_id: id,
      created_by: localStorage.getItem("userId"),
    };
    formData.append("payload", JSON.stringify(payload));
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/save/data-asset-onboarding`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.status === 200 || response.status === 201) {
        setIsSaving(false);
        toast.success("Assets saved successfully!");
      } else {
        toast.error("Failed to save assets.");
        setIsSaving(false);
      }
    } catch (error) {
      // Check for duplicate key constraint error
      if (error.response?.status === 500 && error.response?.data?.detail?.includes("Violation of UNIQUE KEY constraint")) {
        toast.error("This asset is already onboarded with some other product. Please choose different asset for onboarding.");
      } else {
        toast.error("Failed to save. Please try again later. If the issue persists, contact hardik.talati@aaysinsight.com or amitoz.s.sidhu@aaysinsight.com for assistance.");
      }
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Validation check
    const allItemsHaveNames =
      selectedFolders.every((folder) => folder.assetName?.trim()) &&
      uploadedFiles.every((fileObj) => fileObj.assetName?.trim());

    // if (!allItemsHaveNames) {
    //   toast.error("All items must have names before submitting");
    //   return;
    // }

    const formData = new FormData();

    // Append files
    uploadedFiles.forEach((fileObj) => {
      formData.append("files", fileObj.file);
    });
    formData.append("product_id", id);

    // Append other data as JSON
    const payload = {
      selectedFolders: selectedFolders.map((folder) => ({
        ...folder,
        path: folder.path || folder.full_path || folder.name,
        configuration: {
          assetName:
            fileConfigurations[folder.path]?.assetName || folder.assetName,
          assetDescription:
            fileConfigurations[folder.path]?.assetDescription ||
            folder.assetDescription,
          dataAssetOwner:
            fileConfigurations[folder.path]?.dataAssetOwner ||
            folder.dataAssetOwner,
          dataframeName:
            fileConfigurations[folder.path]?.dataframeName ||
            folder.dataframeName,
          region: fileConfigurations[folder.path]?.region || folder.region,
          market: fileConfigurations[folder.path]?.market || folder.market,
          technicalDataSteward:
            fileConfigurations[folder.path]?.technicalDataSteward ||
            folder.technicalDataSteward,
          completeness:
            fileConfigurations[folder.path]?.completeness ||
            folder.completeness,
          uniqueness:
            fileConfigurations[folder.path]?.uniqueness ||
            folder.uniqueness,
          validity:
            fileConfigurations[folder.path]?.validity ||
            folder.validity,
          accuracy:
            fileConfigurations[folder.path]?.accuracy || folder.accuracy,
          consistency:
            fileConfigurations[folder.path]?.consistency || folder.consistency,
          sampleSize:
            fileConfigurations[folder.path]?.sampleSize ||
            folder.sampleSize ||
            10,
          tags: fileConfigurations[folder.path]?.tags || folder.tags || [],
          // Asset Ingestion Schedule Information
          frequency:
            fileConfigurations[folder.path]?.frequency ||
            folder.frequency,
          startDate:
            fileConfigurations[folder.path]?.startDate ||
            folder.startDate,
          endDate:
            fileConfigurations[folder.path]?.endDate ||
            folder.endDate,
          time:
            fileConfigurations[folder.path]?.time ||
            folder.time || "",
          daysOfWeek:
            fileConfigurations[folder.path]?.daysOfWeek ||
            folder.daysOfWeek || [],
          dayOfMonth:
            fileConfigurations[folder.path]?.dayOfMonth ||
            folder.dayOfMonth || "",
          selectedPeriods:
            fileConfigurations[folder.path]?.selectedPeriods ||
            folder.selectedPeriods || [],
        },
      })),
      uploadedFiles: uploadedFiles.map((file) => ({
        ...file,
        configuration: {
          assetName: fileConfigurations[file.id]?.assetName || file.assetName,
          assetDescription:
            fileConfigurations[file.id]?.assetDescription ||
            file.assetDescription,
          dataAssetOwner:
            fileConfigurations[file.id]?.dataAssetOwner || file.dataAssetOwner,
          dataframeName:
            fileConfigurations[file.id]?.dataframeName || file.dataframeName,
          region: fileConfigurations[file.id]?.region || file.region,
          market: fileConfigurations[file.id]?.market || file.market,
          technicalDataSteward:
            fileConfigurations[file.id]?.technicalDataSteward ||
            file.technicalDataSteward,
          completeness:
            fileConfigurations[file.id]?.completeness || file.completeness,
          uniqueness:
            fileConfigurations[file.id]?.uniqueness || file.uniqueness,
          validity:
            fileConfigurations[file.id]?.validity || file.validity,
          accuracy: fileConfigurations[file.id]?.accuracy || file.accuracy,
          consistency:
            fileConfigurations[file.id]?.consistency || file.consistency,
          sampleSize:
            fileConfigurations[file.id]?.sampleSize || file.sampleSize || 10,
          tags: fileConfigurations[file.id]?.tags || file.tags || [],
          // Asset Ingestion Schedule Information
          frequency:
            fileConfigurations[file.id]?.frequency ||
            file.frequency,
          startDate:
            fileConfigurations[file.id]?.startDate ||
            file.startDate,
          endDate:
            fileConfigurations[file.id]?.endDate ||
            file.endDate,
          time:
            fileConfigurations[file.id]?.time ||
            file.time || "",
          daysOfWeek:
            fileConfigurations[file.id]?.daysOfWeek ||
            file.daysOfWeek || [],
          dayOfMonth:
            fileConfigurations[file.id]?.dayOfMonth ||
            file.dayOfMonth || "",
          selectedPeriods:
            fileConfigurations[file.id]?.selectedPeriods ||
            file.selectedPeriods || [],
        },
      })),
      timestamp: new Date().toISOString(),
      totalItems: selectedFolders.length + uploadedFiles.length,
      product_id: id,
      created_by: localStorage.getItem("userId"),
    };
    formData.append("payload", JSON.stringify(payload));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/data-asset-onboarding/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200 || 201) {
        setColumnList(response?.data?.schemas);
        setIsSubmitting(false);
      }

      toast.success("Assets submitted successfully!");

      setTimeout(() => setColumnModalOpen(true), 1000);
    } catch (error) {   
      console.error("Error submitting assets:", error);
      // Check for duplicate key constraint error
      if (error.response?.status === 500 && error.response?.data?.detail?.includes("Violation of UNIQUE KEY constraint")) {
        toast.error("This asset is already onboarded with some other product. Please choose different asset for onboarding.");
      } else {
        toast.error("Failed to submit asset. Please try again later. If the issue persists, contact hardik.talati@aaysinsight.com or amitoz.s.sidhu@aaysinsight.com for assistance.");
      }
      setIsSubmitting(false);
    }
  };

  const handleNameEdit = (id, isFolder, currentName) => {
    if (isFolder) {
      setSelectedFolders((prev) =>
        prev.map((folder) =>
          folder.path === id ? { ...folder, isEditing: true } : folder
        )
      );
    } else {
      setUploadedFiles((prev) =>
        prev.map((fileObj) =>
          fileObj.id === id ? { ...fileObj, isEditing: true } : fileObj
        )
      );
    }
    setEditingNames((prev) => ({ ...prev, [id]: currentName }));
  };

  const handleNameSave = (id, newName, isFolder) => {
    if (isFolder) {
      setSelectedFolders((prev) =>
        prev.map((folder) =>
          folder.path === id
            ? { ...folder, assetName: newName, isEditing: false }
            : folder
        )
      );
    } else {
      setUploadedFiles((prev) =>
        prev.map((fileObj) =>
          fileObj.id === id
            ? { ...fileObj, assetName: newName, isEditing: false }
            : fileObj
        )
      );
    }
    setEditingNames((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const totalItems = selectedFolders.length + uploadedFiles.length;
  const adlsCount = useMemo(
    () => getAllSelectableItems(adlsData, "adls").length,
    [adlsData]
  );
  const ucCount = useMemo(
    () => getAllSelectableItems(ucData, "uc").length,
    [ucData]
  );
  const manualCount = uploadedFiles.length;

  const openConfigModal = (isFile, itemSource, itemKey, itemName) => {
    setConfiguringFileId(itemKey);
    setConfiguringFileName(itemName);
    setConfiguringFileSource(itemSource);
    setIsFile(isFile);
    setConfigModalOpen(true);
  };

  const closeConfigModal = () => {
    setConfiguringFileId(null);
    setConfiguringFileName(null);
    setConfigModalOpen(false);
    setAssetNameCheckMessage("");
    setAssetNameIsUnique(undefined);
    setFormTouched(false);
  };

  // Populate form when opening config modal
  useEffect(() => {
    if (configModalOpen && configuringFileId) {
      const existing = fileConfigurations[configuringFileId] || {};
      const isAlreadyConfigured = Object.keys(existing).length > 0; // Check if configuration already exists
      setIsEditingExistingConfig(isAlreadyConfigured); // Set state for ConfigPopup

      //Pre-populate asset name based on asset source
      let assetNameBySource = "";
      
      if (configuringFileSource === "uc") {
        const filteredData = filterTree(ucData, configuringFileName);
        assetNameBySource = filteredData[0]?.children[0]?.name + "|" + configuringFileName;
      } else if (configuringFileSource === "adls") {
        // For ADLS, use the full path as-is
        assetNameBySource = configuringFileId;
      }
      
      const selectedAssetName = isFile ? "" : assetNameBySource;

      const assetName = existing.assetName || selectedAssetName;
      
      setConfigForm({
        assetName: assetName,
        assetDescription: existing.assetDescription || "",
        dataAssetOwner:
          existing.dataAssetOwner || localStorage.getItem("userId"),
        dataframeName: existing.dataframeName || "",
        tags: existing.tags || [],
        region: existing?.region || (Array.isArray(onboardindDetails?.marketRegion) ? onboardindDetails?.marketRegion.join(', ') : onboardindDetails?.marketRegion),
        market: existing?.market || (Array.isArray(onboardindDetails?.marketCountry) ? onboardindDetails?.marketCountry.join(', ') : onboardindDetails?.marketCountry),
        technicalDataSteward: existing.technicalDataSteward || "",
        completeness: existing.completeness || "",
        uniqueness: existing.uniqueness || "",
        validity: existing.validity || "",
        accuracy: existing.accuracy || "",
        consistency: existing.consistency || "",
        sampleSize: existing.sampleSize || 10,
        // Asset Ingestion Schedule Information
        frequency: existing.frequency || "",
        startDate: existing.startDate || "",
        endDate: existing.endDate || "",
        time: existing.time || "",
        daysOfWeek: existing.daysOfWeek || [],
        dayOfMonth: existing.dayOfMonth || "",
        selectedPeriods: existing.selectedPeriods || [],
      });
      setFormErrors({});
      
      // Only trigger asset name uniqueness check for NEW configurations (not already saved)
      if (assetName && !isAlreadyConfigured) {
        checkAssetNameUnique(assetName);
      } else if (isAlreadyConfigured) {
        // For already configured items, mark as valid so fields are enabled
        setAssetNameIsUnique(true);
        setDisableAllFields(false);
      }
    }
  }, [configModalOpen, configuringFileId]);

  const handleConfigChange = (e) => {
    const { name, value } = e.target;

    // Validate numeric fields to ensure they don't exceed 100
    if (
      ["completeness", "accuracy", "consistency", "sampleSize"].includes(name)
    ) {
      const numValue = Number(value);
      if (numValue > 100) {
        // Don't update the form if value exceeds 100
        return;
      }
    }

    setConfigForm((prev) => ({ ...prev, [name]: value }));
    if (name === "assetName") {
      setAssetNameCheckMessage("");
      setAssetNameIsUnique(undefined);
      if (debounceTimeout) clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        checkAssetNameUnique(value);
      }, 600);
    }
  };

  const checkAssetNameUnique = async (name) => {
    if (!name) {
      setAssetNameCheckMessage("");
      setAssetNameIsUnique(undefined);
      setDisableAllFields(true);
      return;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/data-asset/check?name=${name}`
      );
      if (response.data) {
        setAssetNameCheckMessage(response.data.message);
        setAssetNameIsUnique(!response.data.matched);
        setDisableAllFields(response.data.matched);
      } else {
        setAssetNameCheckMessage("");
        setAssetNameIsUnique(undefined);
        setDisableAllFields(true);
      }
    } catch (error) {
      setAssetNameCheckMessage("Error checking asset name");
      setAssetNameIsUnique(undefined);
      setDisableAllFields(true);
    }
  };

  // useEffect(() => {
  //   checkAssetNameUnique(configForm.assetName);
  // }, [configForm.assetName]);

  useEffect(() => {
    // Disable all fields if asset name is not unique or empty
    if (!configForm.assetName || assetNameIsUnique === false) {
      setDisableAllFields(true);
    } else {
      setDisableAllFields(false);
    }
  }, [configForm.assetName, assetNameIsUnique]);

  const validateConfigForm = () => {
    const errors = {};
    if (!configForm.assetName) errors.assetName = "Required";
    if (!configForm.assetDescription) errors.assetDescription = "Required";
    if (!configForm.dataAssetOwner) {
      errors.dataAssetOwner = "Required";
    } else {
      // Email format validation - only @aaysinsight.com domain allowed
      const ALLOWED_EMAIL_DOMAINS = ['aaysinsight.com'];
      const emailPattern = /^[^\s@]+@([^\s@]+)$/;
      const match = configForm.dataAssetOwner.match(emailPattern);
      if (!match || !ALLOWED_EMAIL_DOMAINS.includes(match[1])) {
        errors.dataAssetOwner = `Email must be from: ${ALLOWED_EMAIL_DOMAINS.join(', ')}`;
      }
    }
    if (!configForm.dataframeName) errors.dataframeName = "Required";
    if (!configForm.region) errors.region = "Required";
    if (!configForm.market) errors.market = "Required";
    if (!configForm.technicalDataSteward) {
      errors.technicalDataSteward = "Required";
    } else {
      // Email format validation - only @aaysinsight.com domain allowed
      const ALLOWED_EMAIL_DOMAINS = ['aaysinsight.com'];
      const emailPattern = /^[^\s@]+@([^\s@]+)$/;
      const match = configForm.technicalDataSteward.match(emailPattern);
      if (!match || !ALLOWED_EMAIL_DOMAINS.includes(match[1])) {
        errors.technicalDataSteward = `Email must be from: ${ALLOWED_EMAIL_DOMAINS.join(', ')}`;
      }
    }
    if (!configForm.completeness || Number(configForm.completeness) < 0)
      errors.completeness = "Min value is 0";
    if (!configForm.uniqueness || Number(configForm.uniqueness) < 0)
      errors.uniqueness = "Min value is 0";
    if (!configForm.validity || Number(configForm.validity) < 0)
      errors.validity = "Min value is 0";
    if (!configForm.accuracy || Number(configForm.accuracy) < 0)
      errors.accuracy = "Min value is 0";
    if (!configForm.consistency || Number(configForm.consistency) < 0)
      errors.consistency = "Min value is 0";
    if (
      !configForm.sampleSize ||
      Number(configForm.sampleSize) < 1 ||
      Number(configForm.sampleSize) > 100
    )
      errors.sampleSize = "Sample size must be between 1 and 100";
    
    // New validation for Schedule Information
    if (!configForm.frequency) errors.frequency = "Required";
    // if (!configForm.startDate) errors.startDate = "Required";
    // if (!configForm.endDate) errors.endDate = "Required";
    
    // Time is required for all non-Hourly frequencies
    if (configForm.frequency && configForm.frequency !== 'Hourly' && !configForm.time) {
      errors.time = "Time is required for selected frequency";
    }

    // Conditional validation based on frequency specifics
    if (configForm.frequency === 'Weekly') {
      if (!configForm.daysOfWeek || configForm.daysOfWeek.length === 0) {
        errors.daysOfWeek = "At least one day must be selected for Weekly frequency";
      }
    }
    if (configForm.frequency === 'Mars Periodic') {
      if (!configForm.dayOfMonth) {
        errors.dayOfMonth = "Day is required for Mars Periodic frequency";
      }
    }
    if (configForm.frequency === 'Mars Quarter') {
      if (!configForm.selectedPeriods || configForm.selectedPeriods.length === 0) {
        errors.selectedPeriods = "At least one period must be selected for Mars Quarter";
      }
      if (!configForm.dayOfMonth) {
        errors.dayOfMonth = "Day is required for Mars Quarter frequency";
      }
    }
    if (configForm.frequency === 'Mars Year') {
      if (!configForm.selectedPeriods || configForm.selectedPeriods.length === 0) {
        errors.selectedPeriods = "One period must be selected for Mars Year";
      }
      if (!configForm.dayOfMonth) {
        errors.dayOfMonth = "Day is required for Mars Year frequency";
      }
    }
    if (configForm.frequency === 'Mars Half-Yearly') {
      if (!configForm.selectedPeriods || configForm.selectedPeriods.length === 0) {
        errors.selectedPeriods = "At least one period must be selected for Mars Half-Yearly";
      }
      if (!configForm.dayOfMonth) {
        errors.dayOfMonth = "Day is required for Mars Half-Yearly frequency";
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Helper to check if all required fields are filled (not format-validated)
  const areAllFieldsFilled = () => {
    // Validate Data Asset Owner email domain
    const isDataAssetOwnerValid = () => {
      if (!configForm.dataAssetOwner) return false;
      const ALLOWED_EMAIL_DOMAINS = ['aaysinsight.com'];
      const emailPattern = /^[^\s@]+@([^\s@]+)$/;
      const match = configForm.dataAssetOwner.match(emailPattern);
      return match && ALLOWED_EMAIL_DOMAINS.includes(match[1]);
    };

    // Validate Technical Data Steward email domain
    const isTechnicalStewardValid = () => {
      if (!configForm.technicalDataSteward) return false;
      const ALLOWED_EMAIL_DOMAINS = ['aaysinsight.com'];
      const emailPattern = /^[^\s@]+@([^\s@]+)$/;
      const match = configForm.technicalDataSteward.match(emailPattern);
      return match && ALLOWED_EMAIL_DOMAINS.includes(match[1]);
    };

    const baseFields = (
      configForm.assetName &&
      configForm.assetDescription &&
      isDataAssetOwnerValid() &&
      configForm.dataframeName &&
      configForm.region &&
      configForm.market &&
      isTechnicalStewardValid() &&
      configForm.completeness &&
      configForm.uniqueness &&
      configForm.validity &&
      configForm.accuracy &&
      configForm.consistency &&
      configForm.sampleSize &&
      configForm.frequency
      // configForm.startDate &&
      // configForm.endDate
    );
    
    // Time required for all non-Hourly
    const needsTime = configForm.frequency && configForm.frequency !== 'Hourly';
    const timeOk = needsTime ? !!configForm.time : true;

    if (configForm.frequency === 'Weekly') {
      return baseFields && timeOk && configForm.daysOfWeek && configForm.daysOfWeek.length > 0;
    }
    if (configForm.frequency === 'Mars Periodic') {
      return baseFields && timeOk && configForm.dayOfMonth;
    }
    if (configForm.frequency === 'Mars Quarter') {
      return baseFields && timeOk && configForm.selectedPeriods && configForm.selectedPeriods.length > 0 && configForm.dayOfMonth;
    }
    if (configForm.frequency === 'Mars Yearly') {
      return baseFields && timeOk && configForm.selectedPeriods && configForm.selectedPeriods.length > 0 && configForm.dayOfMonth;
    }
    if (configForm.frequency === 'Mars Half-Yearly') {
      return baseFields && timeOk && configForm.selectedPeriods && configForm.selectedPeriods.length > 0 && configForm.dayOfMonth;
    }
    if (configForm.frequency === 'Daily') {
      return baseFields && timeOk;
    }
    if (configForm.frequency === 'Hourly') {
      return baseFields; // No additional fields required for Hourly
    }
    
    return baseFields && timeOk;
  };

  useEffect(() => {
    // Enable Save if all fields are filled and not globally disabled
    if (!disableAllFields) {
      setAllFieldsValid(areAllFieldsFilled());
    } else {
      setAllFieldsValid(false);
    }
    // eslint-disable-next-line
  }, [configForm, disableAllFields]);

  const handleConfigSave = () => {
    setFormTouched(true);
    // Now do full validation (including email format)
    if (!validateConfigForm()) return;
    setFileConfigurations((prev) => ({
      ...prev,
      [configuringFileId]: { ...configForm },
    }));
    closeConfigModal();
  };

  // Validation: all selectedFolders and uploadedFiles must be configured
  const allFoldersConfigured = selectedFolders.every(
    (item) => fileConfigurations[item.path]
  );
  const allFilesConfigured = uploadedFiles.every(
    (file) => fileConfigurations[file.id]
  );
  const allItemsConfigured = allFoldersConfigured && allFilesConfigured;

  // Column selection modal logic

  console.log("columnSubmitting", columnSubmitting);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50 w-full h-full">
                   <div className="flex flex-col items-center space-y-3">
                     <img src={preloader} alt="Loading..." className="h-30" />
                     <span className="text-gray-700 text-[20px]">
                     Hang tight! The resources are loading...
                     </span>
                   </div>
                 </div>
    );
  }

  return (
    <>
      {!columnModalOpen && (
        <>
          <div className="flex flex-col  w-full h-[100%] min-h-[600px] p-6 bg-white">
            <div className="p-6  pt-0 border-b">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  {/* <button 
                                onClick={previousStep} 
                                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5" />
                                <span>Back</span>
                            </button> */}
                  <h2 className="text-xl font-bold text-[#0000a0]">
                    Data Asset Selection
                  </h2>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-sm font-medium bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                    {totalItems} total items
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-grow flex overflow-hidden">
              <div className="w-1/3 border-r p-4 flex flex-col">
                <div className="flex gap-2 mb-4">
                  {availableTabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`px-4 py-2 text-sm rounded-md flex items-center gap-2 border ${
                        activeTab === tab.key
                          ? "bg-[#0000a0] text-white border-blue-600"
                          : "bg-white"
                      }`}
                    >
                      {tab.label}
                      <span className="bg-gray-200 text-gray-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {tab.key === "adls"
                          ? adlsCount
                          : tab.key === "uc"
                          ? ucCount
                          : manualCount}
                      </span>
                    </button>
                  ))}
                </div>

                {activeTab !== "manual" && (
                  <>
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search folders..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-10 py-2 border rounded-md"
                      />
                      {searchTerm && (
                        <button
                          type="button"
                          onClick={() => setSearchTerm("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                          tabIndex={-1}
                          aria-label="Clear search"
                        >
                          <X className="h-5" />
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={handleSelectAll}
                        className="px-4 py-2 text-sm rounded-md text-white bg-green-600 hover:bg-green-700"
                      >
                        Select All (
                        {activeTab === "adls"
                          ? adlsCount
                          : activeTab === "uc"
                          ? ucCount
                          : manualCount}
                        )
                      </button>
                      <button
                        onClick={handleClear}
                        className="px-4 py-2 text-sm rounded-md text-white bg-gray-600 hover:bg-gray-700"
                      >
                        Clear Selection
                      </button>
                    </div>
                  </>
                )}

                <div className="flex-grow overflow-y-auto pr-2">
                  {loading && (
                    <div className="flex flex-col justify-center items-center h-full">
                      <CircularProgress />
                      <span className="mt-2 text-gray-700">
                        Loading Data Assets...
                      </span>
                    </div>
                  )}
                  {!loading &&
                    activeTab === "adls" &&
                    (adlsData.length === 0 ? (
                      ErrorMessage?.ADLS ? (
                        <div className="text-red-600 text-center mt-8 font-semibold">
                          {ErrorMessage?.ADLS}
                        </div>
                      ) : (
                        <div className="text-[#0000a0] text-center mt-8 font-semibold">
                          Loading...
                        </div>
                      )
                    ) : (
                      filterTree(adlsData, searchTerm).map((item, index) => (
                        <TreeItem
                          key={index}
                          item={item}
                          source="adls"
                          onSelect={handleItemSelect}
                          selectedItems={selectedFolders}
                          searchTerm={searchTerm}
                          expandedPaths={expandedPaths}
                        />
                      ))
                    ))}
                  {!loading &&
                    activeTab === "uc" &&
                    (ucData.length === 0 ? (
                      ErrorMessage?.UC ? (
                        <div className="text-red-600 text-center mt-8 font-semibold">
                          {ErrorMessage?.UC}
                        </div>
                      ) : (
                        <div className="text-[#0000a0] text-center mt-8 font-semibold">
                          Loading...
                        </div>
                      )
                    ) : (
                      filterTree(ucData, searchTerm).map((item, index) => (
                        <TreeItem
                          key={index}
                          item={item}
                          source="uc"
                          onSelect={handleItemSelect}
                          selectedItems={selectedFolders}
                          searchTerm={searchTerm}
                          expandedPaths={expandedPaths}
                        />
                      ))
                    ))}
                  {activeTab === "manual" && !loading && (
                    <div
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      className={`p-6 border-2 border-dashed rounded-lg text-center cursor-pointer ${
                        isDragActive
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300"
                      }`}
                    >
                      <input
                        id="file-upload"
                        type="file"
                        multiple
                        accept=".csv"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                      <label
                        htmlFor="file-upload"
                        className="flex flex-col items-center justify-center w-full cursor-pointer"
                      >
                        <UploadCloud className="h-12 w-12 mx-auto text-gray-400" />
                        <p className="mt-2 text-sm text-gray-600">
                          Drag and drop your CSV files here, or click to browse
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Only CSV files are accepted
                        </p>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <div className="w-2/3 p-4 flex flex-col">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg">
                    {/* Selected Items ({totalItems}) */}
                  </h3>
                  <div className="flex gap-2">
                    {totalItems > 0 && (
                      <button
                        onClick={handleSave}
                        className={`px-4 py-2 rounded-md ${
                          !allItemsConfigured || isSaving
                            ? "bg-gray-200 text-gray-900"
                            : "bg-green-700 text-white hover:bg-green-800"
                        }`}
                        disabled={!allItemsConfigured || isSaving}
                      >
                        {isSaving ? "Saving..." : "Save"}
                      </button>
                    )}
                    {totalItems > 0 && (
                      <button
                        onClick={handleSubmit}
                        className={`px-4 py-2 rounded-md ${
                          !allItemsConfigured || isSubmitting
                            ? "bg-gray-200 text-gray-900"
                            : "bg-[#0000a0] text-white"
                        }`}
                        disabled={!allItemsConfigured || isSubmitting}
                      >
                        {isSubmitting ? "Submitting..." : "Continue"}
                      </button>
                    )}    
                  </div>
                </div>
                <div className="flex-grow overflow-y-auto">
                  {selectedFolders.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium mb-2">
                        {/* Selected Folders ({selectedFolders.length}) */}
                      </h4>
                      <div className="space-y-2">
                        {selectedFolders.map((folder) => (
                          <div
                            key={folder.path}
                            className="flex items-center justify-between p-3 bg-[#F5FFFA] rounded-lg border"
                          >
                            <div className="flex items-center flex-grow">
                              {folder.source === "uc" ? (
                                <Database className="h-5  mr-3 text-blue-500" />
                              ) : (
                                <Folder className="h-5  mr-3 text-yellow-500" />
                              )}
                              <div className="flex-grow mr-4">
                                {folder.isEditing ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={
                                        editingNames[folder.path] ??
                                        folder.assetName
                                      }
                                      onChange={(e) =>
                                        setEditingNames((prev) => ({
                                          ...prev,
                                          [folder.path]: e.target.value,
                                        }))
                                      }
                                      onBlur={() =>
                                        handleNameSave(
                                          folder.path,
                                          editingNames[folder.path] ||
                                            folder.defaultName,
                                          true
                                        )
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          handleNameSave(
                                            folder.path,
                                            editingNames[folder.path] ||
                                              folder.defaultName,
                                            true
                                          );
                                        }
                                      }}
                                      className="w-full p-1 border rounded"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() =>
                                        handleNameSave(
                                          folder.path,
                                          editingNames[folder.path] ||
                                            folder.defaultName,
                                          true
                                        )
                                      }
                                      className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                    >
                                      Save
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center">
                                    <p className="font-bold">{folder.name}</p>
                                  </div>
                                )}
                                <p className="text-xs text-gray-500">
                                  {/* {folder.source} · {folder.path} */}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() =>
                                  openConfigModal(
                                    false,
                                    folder.source,
                                    folder.path,
                                    folder.name
                                  )
                                }
                                className={`ml-2 p-1 rounded flex gap-1 items-center font-bold p-2 rounded text-base ${
                                  fileConfigurations[folder.path]
                                    ? "bg-green-500 text-white hover:bg-green-600"
                                    : "bg-[#9B00FB] text-white hover:bg-[#9B00FB]"
                                }`}
                              >
                                <span>Configuration</span>
                              </button>
                              <button
                                onClick={() => handleRemoveFolder(folder.path)}
                              >
                                <Trash2 className="h-5  text-red-500 hover:text-red-700" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {uploadedFiles.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">
                        Uploaded Files ({uploadedFiles.length})
                      </h4>
                      <div className="space-y-2">
                        {uploadedFiles.map((fileObj) => (
                          <div
                            key={fileObj.id}
                            className="flex items-center justify-between p-3 bg-[#F5FFFA] rounded-lg border"
                          >
                            <div className="flex items-center flex-grow">
                              <FileText className="h-5 mr-3 text-gray-500" />
                              <div className="flex-grow mr-4">
                                {fileObj.isEditing ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={
                                        editingNames[fileObj.id] ??
                                        fileObj.assetName
                                      }
                                      onChange={(e) =>
                                        setEditingNames((prev) => ({
                                          ...prev,
                                          [fileObj.id]: e.target.value,
                                        }))
                                      }
                                      onBlur={() =>
                                        handleNameSave(
                                          fileObj.id,
                                          editingNames[fileObj.id] ||
                                            fileObj.defaultName,
                                          false
                                        )
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          handleNameSave(
                                            fileObj.id,
                                            editingNames[fileObj.id] ||
                                              fileObj.defaultName,
                                            false
                                          );
                                        }
                                      }}
                                      className="w-full p-1 border rounded"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() =>
                                        handleNameSave(
                                          fileObj.id,
                                          editingNames[fileObj.id] ||
                                            fileObj.defaultName,
                                          false
                                        )
                                      }
                                      className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                    >
                                      Save
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center">
                                    <p className="font-medium">
                                      {fileObj.name}
                                    </p>
                                  </div>
                                )}
                                <p className="text-xs text-gray-500">
                                  {(fileObj.size / 1024).toFixed(2)} KB ·
                                  Uploaded{" "}
                                  {fileObj.lastModified
                                    ? new Date(
                                        fileObj.lastModified
                                      ).toLocaleDateString()
                                    : ""}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() =>
                                  openConfigModal(
                                    true,
                                    "",
                                    fileObj.id,
                                    fileObj.name
                                  )
                                }
                                className={`ml-2 p-1 rounded flex gap-1 items-center font-bold p-2 rounded-md text-base ${
                                  fileConfigurations[fileObj.id]
                                    ? "bg-green-500 text-white hover:bg-green-600"
                                    : "bg-[#9B00FB] text-white hover:bg-[#9B00FB]"
                                }`}
                              >
                                <span>Configuration</span>
                              </button>
                              <button
                                onClick={() => handleRemoveFile(fileObj.id)}
                              >
                                <X className="h-5  text-red-500 hover:text-red-700" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {totalItems === 0 && (
                    <div className="text-center text-gray-500 py-10">
                      <p>No items selected.</p>
                      <p className="text-sm">
                        Select folders, tables or upload files to see them here.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Toaster position="top-right" />
          <ConfigPopup
            configForm={configForm}
            configModalOpen={configModalOpen}
            closeConfigModal={closeConfigModal}
            handleConfigChange={handleConfigChange}
            formErrors={formErrors}
            handleConfigSave={handleConfigSave}
            disableAllFields={disableAllFields}
            assetNameCheckMessage={assetNameCheckMessage}
            assetNameIsUnique={assetNameIsUnique}
            allFieldsValid={allFieldsValid}
            formTouched={formTouched}
            regionsCountriesData={regionsCountriesData}
            setConfigForm={setConfigForm}
            isEditingExistingConfig={isEditingExistingConfig}
          />
        </>
      )}

      {columnModalOpen && (
        // <Dialog open={columnModalOpen} onClose={() => {}} maxWidth="md" fullWidth>
        <ColumnSelection
          nextStep={nextStep}
          columnsList={columnsList}
          id={id}
          setLoading={setLoading}
        />
        // </Dialog>
      )}
    </>
  );
};

export default AssetSelectionModal;


